import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "../../i18n/TranslationProvider";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import sendMessageSchema from "./validations/sendMessageSchema";

import { Col, Form, Row } from "react-bootstrap";
import ComponentWrapper from "./ComponentWrapper";
import LabelledInput from "../LabelledInput";
import SubmitButton from "../StyledComponents/SubmitButton";

import { sendMessage, deleteVideo, deleteImage } from "../../api/messageRequests";

import VideoUploadIcon from "../../images/video-upload.svg";
import ImageUploadIcon from "../../images/image-upload.svg";

import ImageUploader from "./ImageUploader";
import VideoUploader from "./VideoUploader";

const SendMessage = ({ recipientId, recipientName, virtualTherapistName, virtualTherapistId, userType, replyTo, onMessageSent, onMediaCanceled }) => {
    const { t } = useTranslation();
    const [showInput, setShowInput] = useState(true);
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const [activeUploader, setActiveUploader] = useState(null);
    const [uploaderStatus, setUploaderStatus] = useState("idle");
    const [mediaUid, setMediaUid] = useState(null);
    const [mediaType, setMediaType] = useState(null);

    const messageSentRef = useRef(false);
    const manualCancelRef = useRef(false);

    const { register, handleSubmit, errors } = useForm({
        resolver: yupResolver(sendMessageSchema),
    });

    // delete media if user cancels without sending, runs when sendMessage unmounts (the modal closes)
    useEffect(() => {
    return () => {
        if (!messageSentRef.current && mediaUid && mediaType) {
            if (mediaType === "video") {
                deleteVideo(mediaUid).catch(console.error);
              
            }
            if (mediaType === "image") {
                deleteImage(mediaUid).catch(console.error);
            
                
            }
        
            // tell Messages.js to display the modal
            // Only notify the pop-up if the sendMessage modal closed on its own,not because the user manually hit × on the uploader
            if (!manualCancelRef.current && onMediaCanceled) {
                onMediaCanceled();
            }
             
        }
    };
}, [mediaUid, mediaType]);



    const onSubmit = (data) => {
        setSubmitDisabled(true);
        data.userType = userType;
        if (mediaUid) {
            data.mediaUid = mediaUid;
            data.mediaType = mediaType;
        }

        const basePayload = {
            body: data.body,
            userType: data.userType,
            ...(data.mediaUid ? { mediaUid: data.mediaUid, mediaType: data.mediaType } : {}),
        };

        const providerCall = sendMessage({
            ...basePayload,
            recipientId,
            // If also sending to VT, suppress the provider email — only VT gets notified
            ...(virtualTherapistId ? { suppressEmail: true } : {}),
        });
        const vtCall = virtualTherapistId
            ? sendMessage({ ...basePayload, recipientId: virtualTherapistId })
            : Promise.resolve(null);

        console.log("[SendMessage] Submitting...", { recipientId, virtualTherapistId, userType });

        Promise.all([providerCall, vtCall])
            .then(([providerRes]) => {
                console.log("[SendMessage] Message(s) sent! Provider response:", providerRes);
                messageSentRef.current = true;
                if (onMessageSent) {
                    console.log("[SendMessage] Calling onMessageSent callback");
                    onMessageSent(
                        {
                            ...(providerRes?.data || {}),
                            body: data.body,
                            mediaUid: data.mediaUid ?? null,
                            mediaType: data.mediaType ?? null,
                        },
                        recipientName
                    );
                } else {
                    console.warn("[SendMessage] onMessageSent is not defined — no callback fired!");
                }
            })
            .catch((err) => {
                console.error("[SendMessage] Send FAILED:", err);
            })
            .finally(() => {
                setSubmitDisabled(false);
            });
    };

    return (
        <div>
            {recipientId ? (
                showInput && (
                    <>
                        {replyTo && (
                            <div className="p-2">
                             
                                    <p>{t("messages.ui.replyingTo", { name: recipientName })}</p>
                                    <div
                                        style={{
                                            backgroundColor: "#f5f5f5",
                                            fontWeight: 500,
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            padding: "0.5rem",
                                            border: "1px solid #ccc",
                                            marginBottom: "1rem",
                                            whiteSpace: "pre-wrap",
                                        }}
                                    >
                                        {replyTo.body}
                                    </div>
                              </div>
                        )}
                        <div className="p-2">
                       
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <LabelledInput
                                        required
                                        name="body"
                                        label={
                                            virtualTherapistName
                                                ? `${t("messages.sendMessages.sendMessageTo")}${virtualTherapistName} ${t("messages.sendMessages.virtualTherapistLabel")} ${t("messages.sendMessages.and")} ${recipientName} ${t("messages.sendMessages.providerLabel")}`
                                                : `${t("messages.sendMessages.sendMessageTo")}${recipientName}`
                                        }
                                        errors={errors}
                                        inputRef={register}
                                        inputAs="textarea"
                                        rows={5}
                                    />
                                    {userType !== "provider" && (
                                    <div className="d-flex align-items-center gap-3 mt-1">
                                        <img
                                            src={ImageUploadIcon}
                                            alt={t("messages.ui.uploadImage")}
                                            title={t("messages.ui.uploadImage")}
                                            className="upload-photo-icon"
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                                setActiveUploader(
                                                    activeUploader === "image" ? null : "image"
                                                )
                                            }
                                        />
                                        <img
                                            src={VideoUploadIcon}
                                            alt={t("messages.ui.uploadVideo")}
                                            title={t("messages.ui.uploadVideo")}
                                            className="upload-video-icon"
                                            style={{ cursor: "pointer" }}
                                            onClick={() =>
                                                setActiveUploader(
                                                    activeUploader === "video" ? null : "video"
                                                )
                                            }
                                        />
                                    </div>
                                    )}
                                    {userType !== "provider" && activeUploader === "image" && (
                                        <ImageUploader
                                            onUploadComplete={(uid) => {
                                                setMediaUid(uid);
                                                setMediaType(uid ? "image" : null);
                                            }}
                                            onStatusChange={setUploaderStatus}
                                            onManualCancel={() => { manualCancelRef.current = true; }}
                                        />
                                    )}
                                    {userType !== "provider" && activeUploader === "video" && (
                                        <VideoUploader
                                            onUploadComplete={(uid) => {
                                                setMediaUid(uid);
                                                setMediaType(uid ? "video" : null);
                                                
                                            }}
                                            onStatusChange={setUploaderStatus}
                                            onManualCancel={() => { manualCancelRef.current = true; }}
                                        />
                                    )}
                                    <SubmitButton
                                        type="submit"
                                        disabled={submitDisabled || uploaderStatus === "uploading"}
                                        className="mt-2"
                                    >
                                        {t("messages.sendMessages.send")}
                                    </SubmitButton>
                                </Form>
                           </div>
                    </>
                )
            ) : (
                <p>{t("messages.sendMessages.selectProvider")}</p>
            )}
        </div>
    );
};

export default SendMessage;